import { Suspense, lazy, useEffect } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import type { Role } from '@jobshop/shared';
import { useAppearance } from './api/hooks';
import { useSession } from './auth/useUser';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Spinner } from './components/ui';
import { BoardPage } from './features/board/BoardPage';
import { JobsPage } from './features/jobs/JobsPage';
import { JobPage } from './features/jobs/JobPage';
import { JobListPage } from './features/jobs/JobListPage';
import { JobScansPage } from './features/jobs/JobScansPage';
import { PartsPage } from './features/parts/PartsPage';
import { SignInPage } from './features/SignInPage';

// Split off the two heaviest, least universal screens: the camera scanner is a
// quarter of the bundle and a planner never opens it, and vice versa for admin. The
// Scans page itself is light — it splits the scanner off again behind its own button.
const ScansPage = lazy(() =>
  import('./features/scan/ScansPage').then((m) => ({ default: m.ScansPage })),
);
const KioskPage = lazy(() =>
  import('./features/scan/KioskPage').then((m) => ({ default: m.KioskPage })),
);
const AdminPage = lazy(() =>
  import('./features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const LabelsPage = lazy(() =>
  import('./features/parts/LabelsPage').then((m) => ({ default: m.LabelsPage })),
);
// Opened in its own window from the board, so it is only ever loaded on demand.
const CalendarPage = lazy(() =>
  import('./features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);

interface NavItem {
  to: string;
  label: string;
  role: Role;
}

const NAV: NavItem[] = [
  { to: '/board', label: 'Board', role: 'planner' },
  { to: '/jobs', label: 'Jobs', role: 'viewer' },
  { to: '/scans', label: 'Scans', role: 'operator' },
  { to: '/admin', label: 'Admin', role: 'admin' },
];

export function App() {
  const session = useSession();
  const location = useLocation();
  const appearance = useAppearance(!!session.me);

  // Shop-wide, not per-user: scaling the root font size grows Tailwind's rem-based
  // spacing right along with the text, the same technique as a browser's own zoom.
  useEffect(() => {
    document.documentElement.style.fontSize = appearance.data ? `${appearance.data.fontScale * 100}%` : '';
  }, [appearance.data]);

  if (session.loading) return <Spinner label="Signing in…" />;
  if (!session.me) return <SignInPage />;

  // Operators land on Scans, planners on the Board.
  const home = session.can('planner') ? '/board' : '/scans';
  // The kiosk is the whole screen — no header, no nav, nothing to tap by accident.
  const kiosk = location.pathname === '/kiosk';
  // The month calendar opens in a window of its own, so it carries no app chrome
  // either — but it is a page to read, not a kiosk, so it keeps its padding.
  const popout = location.pathname === '/calendar';

  return (
    <div className="min-h-dvh">
      {kiosk || popout ? null : (
        <header className="no-print sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
            <span className="text-sm font-semibold tracking-tight">Jobshop</span>
            <nav className="flex items-center gap-1" aria-label="Main">
              {NAV.filter((item) => session.can(item.role)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(
                      'rounded-md px-2.5 py-1 text-sm font-medium',
                      isActive ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-3 text-xs text-ink-500">
              <span title={session.me.email}>
                {session.me.email} · {session.me.role}
                {session.me.stationKey ? ` · ${session.me.stationKey}` : ''}
              </span>
              <button type="button" className="btn-secondary" onClick={() => void session.signOut()}>
                Sign out
              </button>
            </div>
          </div>
        </header>
      )}

      <main
        className={clsx(popout && 'px-3 py-3', !kiosk && !popout && 'mx-auto max-w-[1600px] px-4 py-4')}
      >
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={<Spinner />}>
            <Routes>
            <Route path="/" element={<Navigate to={home} replace />} />
            <Route path="/board" element={<Guard role="planner"><BoardPage /></Guard>} />
            <Route path="/jobs" element={<Guard role="viewer"><JobsPage /></Guard>} />
            <Route path="/jobs/:jobId" element={<Guard role="viewer"><JobPage /></Guard>} />
            <Route path="/jobs/:jobId/list" element={<Guard role="viewer"><JobListPage /></Guard>} />
            <Route path="/jobs/:jobId/scans" element={<Guard role="viewer"><JobScansPage /></Guard>} />
            <Route
              path="/jobs/:jobId/batches/:batchId/parts"
              element={<Guard role="viewer"><PartsPage /></Guard>}
            />
            <Route
              path="/jobs/:jobId/batches/:batchId/labels"
              element={<Guard role="viewer"><LabelsPage /></Guard>}
            />
            <Route path="/scans" element={<Guard role="operator"><ScansPage /></Guard>} />
            {/* The old address, still on phones' home screens and in bookmarks. */}
            <Route path="/scan" element={<Navigate to="/scans" replace />} />
            <Route path="/kiosk" element={<Guard role="operator"><KioskPage /></Guard>} />
            <Route path="/calendar" element={<Guard role="planner"><CalendarPage /></Guard>} />
            <Route path="/admin" element={<Guard role="admin"><AdminPage /></Guard>} />
            <Route path="*" element={<Navigate to={home} replace state={{ from: location }} />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

/** Role gating in the client is a courtesy; the server enforces it on every route. */
function Guard({ role, children }: { role: Role; children: React.ReactNode }) {
  const session = useSession();
  if (!session.can(role)) {
    return (
      <p className="card p-6 text-sm text-ink-600">
        This page needs the <strong>{role}</strong> role. You are signed in as{' '}
        <strong>{session.me?.role}</strong>.
      </p>
    );
  }
  return <>{children}</>;
}
